import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Card } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import {
  ChartLine,
  Users,
  Package,
  Gear,
  Palette,
  ShoppingCart,
  Ticket,
  UserCircle,
  UserCheck,
  Percent,
  Receipt,
  CheckCircle,
  XCircle,
  Image as ImageIcon,
} from '@phosphor-icons/react';
import { AnalyticsPanel } from '../components/AnalyticsPanel';
import { AdminSidebar, ADMIN_NAV } from '../components/admin/AdminSidebar';
import { OverviewPanel } from '../components/admin/OverviewPanel';
import { HealthPanel } from '../components/admin/HealthPanel';
import { ServicesPanel } from '../components/admin/ServicesPanel';
import { OrdersPanel as OrdersPanelPro } from '../components/admin/OrdersPanel';
import { IntegrationsPanel } from '../components/admin/IntegrationsPanel';
import { ContentPanel } from '../components/admin/ContentPanel';
import { UsersPanel as UsersPanelPro } from '../components/admin/UsersPanel';
import { LogsPanel } from '../components/admin/LogsPanel';
import { ToolsPanel } from '../components/admin/ToolsPanel';
import { InvoicingPanel } from '../components/admin/InvoicingPanel';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const AdminDashboard = () => {
  const [searchParams] = useSearchParams();
  const focusOrderId = searchParams.get('order');
  const initialTab = focusOrderId ? 'payments' : 'overview';
  const [activeTab, setActiveTab] = useState(initialTab);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [stats, setStats] = useState(null);
  const [settings, setSettings] = useState({ smm_api: {}, branding: {} });
  const [orders, setOrders] = useState([]);
  const [users, setUsers] = useState([]);
  const [services, setServices] = useState([]);
  const [customers, setCustomers] = useState({ registered: [], guests: [] });
  const [pendingPayments, setPendingPayments] = useState([]);
  const [tracking, setTracking] = useState({ meta_pixel_id: '', active: true });
  const [loading, setLoading] = useState(true);
  const [discountInputs, setDiscountInputs] = useState({});

  useEffect(() => {
    fetchAllData();
  }, []);

  // 🎯 Deep link desde Telegram: /admin?order=XXXX resalta y scrollea al pedido
  useEffect(() => {
    if (!focusOrderId || loading) return;
    // esperar al render de la lista
    const t = setTimeout(() => {
      const el = document.querySelector(`[data-testid="pending-payment-${focusOrderId}"]`);
      if (el && typeof el.scrollIntoView === 'function') {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        el.classList.add('ring-2', 'ring-primary', 'ring-offset-2', 'ring-offset-background');
        setTimeout(() => {
          el.classList.remove('ring-2', 'ring-primary', 'ring-offset-2', 'ring-offset-background');
        }, 4000);
      }
    }, 250);
    return () => clearTimeout(t);
  }, [focusOrderId, loading, pendingPayments.length]);

  const fetchAllData = async () => {
    try {
      const [statsRes, settingsRes, ordersRes, usersRes, servicesRes, customersRes, paymentsRes, trackingRes] = await Promise.all([
        axios.get(`${API_URL}/api/admin/stats`, { withCredentials: true }),
        axios.get(`${API_URL}/api/admin/settings`, { withCredentials: true }),
        axios.get(`${API_URL}/api/admin/orders`, { withCredentials: true }),
        axios.get(`${API_URL}/api/admin/users`, { withCredentials: true }),
        axios.get(`${API_URL}/api/admin/services`, { withCredentials: true }),
        axios.get(`${API_URL}/api/admin/customers`, { withCredentials: true }),
        axios.get(`${API_URL}/api/admin/orders/awaiting-payment`, { withCredentials: true }),
        axios.get(`${API_URL}/api/admin/tracking`, { withCredentials: true }),
      ]);
      setStats(statsRes.data);
      setSettings(settingsRes.data);
      setOrders(ordersRes.data);
      setUsers(usersRes.data);
      setServices(servicesRes.data);
      setCustomers(customersRes.data);
      setPendingPayments(paymentsRes.data);
      setTracking(trackingRes.data || { meta_pixel_id: '', active: true });
    } catch (error) {
      toast.error('Error al cargar datos');
    } finally {
      setLoading(false);
    }
  };

  const updateTracking = async (payload) => {
    try {
      const { data } = await axios.post(
        `${API_URL}/api/admin/tracking`,
        payload,
        { withCredentials: true }
      );
      setTracking(data);
      toast.success('Meta Pixel actualizado');
    } catch (error) {
      const msg = error?.response?.data?.detail || 'Error al guardar el pixel';
      toast.error(typeof msg === 'string' ? msg : JSON.stringify(msg));
    }
  };

  const approvePayment = async (orderId) => {
    try {
      const { data } = await axios.post(
        `${API_URL}/api/admin/orders/${orderId}/approve-payment`,
        {},
        { withCredentials: true }
      );
      if (data.success) {
        toast.success(`Pago aprobado. Pedido en panel: ${data.external_order_id}`);
      } else {
        toast.error(`Error: ${data.error}`);
      }
      fetchAllData();
    } catch (error) {
      toast.error('Error al aprobar pago');
    }
  };

  const rejectPayment = async (orderId) => {
    try {
      await axios.post(
        `${API_URL}/api/admin/orders/${orderId}/reject-payment`,
        {},
        { withCredentials: true }
      );
      toast.success('Pago rechazado');
      fetchAllData();
    } catch (error) {
      toast.error('Error al rechazar pago');
    }
  };

  const saveUserDiscount = async (userId, discountPct) => {
    try {
      await axios.post(
        `${API_URL}/api/admin/users/${userId}/discount`,
        { discount_percentage: parseFloat(discountPct) || 0 },
        { withCredentials: true }
      );
      toast.success('Descuento actualizado');
      fetchAllData();
    } catch (error) {
      toast.error('Error al guardar descuento');
    }
  };

  const updateSettings = async (apiUrl, apiKey) => {
    try {
      await axios.post(
        `${API_URL}/api/admin/settings`,
        { smm_api_url: apiUrl, smm_api_key: apiKey },
        { withCredentials: true }
      );
      toast.success('Configuración actualizada');
      fetchAllData();
    } catch (error) {
      toast.error('Error al actualizar configuración');
    }
  };

  const toggleServiceStatus = async (serviceId, currentStatus) => {
    try {
      await axios.patch(
        `${API_URL}/api/admin/services/${serviceId}`,
        { enabled: !currentStatus },
        { withCredentials: true }
      );
      toast.success('Servicio actualizado');
      fetchAllData();
    } catch (error) {
      toast.error('Error al actualizar servicio');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen notranslate" translate="no" data-testid="admin-dashboard-root">
      <div className="flex">
        <AdminSidebar
          active={activeTab}
          onChange={setActiveTab}
          pendingCount={pendingPayments.length}
          collapsed={sidebarCollapsed}
          onToggle={() => setSidebarCollapsed((c) => !c)}
        />

        <main className="flex-1 min-w-0 py-8 px-4 sm:px-6 lg:px-8">
          <div className="mb-6 flex items-center justify-between flex-wrap gap-2">
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold" data-testid="admin-dashboard-title">
                Panel de Administración
              </h1>
              <p className="text-muted-foreground text-sm">
                {ADMIN_NAV.find((n) => n.key === activeTab)?.label || 'Gestión'}
              </p>
            </div>
          </div>

          <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
            <TabsList className="sr-only" aria-hidden="true">
              <TabsTrigger value="overview">Overview</TabsTrigger>
              <TabsTrigger value="stats">Stats</TabsTrigger>
              <TabsTrigger value="analytics">Analytics</TabsTrigger>
              <TabsTrigger value="payments">Payments</TabsTrigger>
              <TabsTrigger value="orders">Orders</TabsTrigger>
              <TabsTrigger value="customers">Customers</TabsTrigger>
              <TabsTrigger value="users">Users</TabsTrigger>
              <TabsTrigger value="services">Services</TabsTrigger>
              <TabsTrigger value="pricing">Pricing</TabsTrigger>
              <TabsTrigger value="settings">Settings</TabsTrigger>
              <TabsTrigger value="branding">Branding</TabsTrigger>
              <TabsTrigger value="content">Content</TabsTrigger>
              <TabsTrigger value="integrations">Integrations</TabsTrigger>
              <TabsTrigger value="logs">Logs</TabsTrigger>
              <TabsTrigger value="tools">Tools</TabsTrigger>
              <TabsTrigger value="health">Health</TabsTrigger>
              <TabsTrigger value="invoicing">Invoicing</TabsTrigger>
            </TabsList>

            {/* NEW PANELS ================================================ */}
            <TabsContent value="overview" className="mt-0"><OverviewPanel /></TabsContent>
            <TabsContent value="invoicing" className="mt-0"><InvoicingPanel /></TabsContent>
            <TabsContent value="services" className="mt-0"><ServicesPanel /></TabsContent>
            <TabsContent value="orders" className="mt-0"><OrdersPanelPro /></TabsContent>
            <TabsContent value="users" className="mt-0"><UsersPanelPro /></TabsContent>
            <TabsContent value="content" className="mt-0"><ContentPanel /></TabsContent>
            <TabsContent value="integrations" className="mt-0">
              <IntegrationsPanel initialSmm={settings.smm_api} initialTracking={tracking} onSaved={fetchAllData} />
            </TabsContent>
            <TabsContent value="logs" className="mt-0"><LogsPanel /></TabsContent>
            <TabsContent value="tools" className="mt-0"><ToolsPanel /></TabsContent>
            <TabsContent value="health" className="mt-0"><HealthPanel /></TabsContent>
            <TabsContent value="pricing" className="mt-0">
              <div className="space-y-4">
                <h2 className="text-2xl font-bold">Precios & Promos</h2>
                <p className="text-sm text-muted-foreground">
                  Editá precios por paquete desde <button onClick={() => setActiveTab('services')} className="text-primary underline">Servicios</button>.
                  Los cupones y promociones se administran directamente sobre la base — más abajo tenés el resumen actual.
                </p>
                <div className="p-6 rounded-lg border border-dashed border-border text-center text-muted-foreground text-sm">
                  Módulo de cupones/promos completo próximamente.
                </div>
              </div>
            </TabsContent>

            {/* EXISTING PANELS (legacy content preserved) ===================== */}
            <TabsContent value="stats" className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              <Card className="p-6 border-border">
                <div className="flex items-center justify-between mb-4">
                  <Users size={32} className="text-primary" weight="duotone" />
                  <Badge variant="secondary">{stats?.total_users || 0}</Badge>
                </div>
                <h3 className="text-2xl font-bold mb-1">{stats?.total_users || 0}</h3>
                <p className="text-sm text-muted-foreground">Usuarios totales</p>
              </Card>

              <Card className="p-6 border-border">
                <div className="flex items-center justify-between mb-4">
                  <ShoppingCart size={32} className="text-primary" weight="duotone" />
                  <Badge variant="secondary">{stats?.total_orders || 0}</Badge>
                </div>
                <h3 className="text-2xl font-bold mb-1">{stats?.total_orders || 0}</h3>
                <p className="text-sm text-muted-foreground">Pedidos totales</p>
              </Card>

              <Card className="p-6 border-border">
                <div className="flex items-center justify-between mb-4">
                  <ChartLine size={32} className="text-primary" weight="duotone" />
                  <Badge variant="secondary">${(stats?.total_revenue || 0).toLocaleString('es-AR')}</Badge>
                </div>
                <h3 className="text-2xl font-bold mb-1">${(stats?.total_revenue || 0).toLocaleString('es-AR')}</h3>
                <p className="text-sm text-muted-foreground">Ingresos totales (ARS)</p>
              </Card>

              <Card className="p-6 border-border bg-yellow-500/5">
                <h3 className="text-2xl font-bold mb-1">{stats?.pending_orders || 0}</h3>
                <p className="text-sm text-muted-foreground">Pedidos pendientes</p>
              </Card>

              <Card className="p-6 border-border bg-blue-500/5">
                <h3 className="text-2xl font-bold mb-1">{stats?.processing_orders || 0}</h3>
                <p className="text-sm text-muted-foreground">Pedidos en proceso</p>
              </Card>

              <Card className="p-6 border-border bg-green-500/5">
                <h3 className="text-2xl font-bold mb-1">{stats?.completed_orders || 0}</h3>
                <p className="text-sm text-muted-foreground">Pedidos completados</p>
              </Card>
            </div>
          </TabsContent>

          {/* Analytics Tab - Live visitor stats */}
          <TabsContent value="analytics" className="space-y-4" data-testid="analytics-tab-content">
            <AnalyticsPanel />
          </TabsContent>

          {/* Payments Tab - Receipts verification */}
          <TabsContent value="payments" className="space-y-4" data-testid="payments-tab-content">
            {pendingPayments.length === 0 ? (
              <Card className="p-12 text-center border-border">
                <Receipt size={48} weight="duotone" className="mx-auto mb-3 text-muted-foreground" />
                <h3 className="font-semibold mb-1">No hay pagos pendientes</h3>
                <p className="text-sm text-muted-foreground">
                  Los pedidos esperando verificación aparecerán acá
                </p>
              </Card>
            ) : (
              <>
                <div className="flex items-center justify-between p-4 rounded-xl border border-primary/20 bg-primary/5">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-primary/15 flex items-center justify-center">
                      <Receipt size={22} weight="duotone" className="text-primary" />
                    </div>
                    <div>
                      <div className="font-semibold">Pagos esperando tu aprobación</div>
                      <div className="text-xs text-muted-foreground">
                        Revisá cada comprobante antes de enviar el pedido al panel SMM
                      </div>
                    </div>
                  </div>
                  <Badge className="bg-primary text-primary-foreground border-0 text-base px-3 py-1">
                    {pendingPayments.length}
                  </Badge>
                </div>

                {pendingPayments.map((p) => {
                  const analysis = p.receipt_analysis || {};
                  const ocrFlags = [
                    { ok: analysis.destination_matches, label: 'Destinatario' },
                    { ok: analysis.amount_matches, label: 'Monto exacto' },
                    { ok: analysis.date_matches_today, label: 'Fecha de hoy' },
                  ];
                  const okCount = ocrFlags.filter((f) => f.ok).length;
                  const ocrBadge =
                    okCount === 3
                      ? { text: 'OCR ✓ (3/3)', cls: 'bg-[#00E676]/15 text-[#00E676]' }
                      : okCount === 0
                      ? { text: `OCR ✗ (0/3)`, cls: 'bg-destructive/15 text-destructive' }
                      : { text: `OCR ⚠ (${okCount}/3)`, cls: 'bg-yellow-500/15 text-yellow-600' };

                  return (
                    <Card
                      key={p.id}
                      className="overflow-hidden border-border"
                      data-testid={`pending-payment-${p.id}`}
                    >
                      <div className="grid grid-cols-1 lg:grid-cols-5 gap-0">
                        {/* Columna 1-2: Datos del pedido */}
                        <div className="lg:col-span-2 p-6 border-r border-border bg-muted/30">
                          <div className="flex items-center gap-2 mb-4 flex-wrap">
                            <Badge className="bg-yellow-500/15 text-yellow-600 border-0">
                              Revisión manual
                            </Badge>
                            {p.receipt_uploaded && (
                              <Badge className={`${ocrBadge.cls} border-0`}>{ocrBadge.text}</Badge>
                            )}
                            {p.is_first_purchase_bonus && (
                              <Badge className="bg-[#00E676]/15 text-[#00E676] border-0">Bono aplicado</Badge>
                            )}
                          </div>

                          <div className="space-y-3 mb-4">
                            <div>
                              <div className="text-[10px] uppercase tracking-wider text-muted-foreground mb-0.5">
                                Servicio
                              </div>
                              <div className="font-semibold text-base">{p.service_name}</div>
                            </div>
                            <div className="grid grid-cols-2 gap-3">
                              <div>
                                <div className="text-[10px] uppercase tracking-wider text-muted-foreground mb-0.5">
                                  Cliente
                                </div>
                                <div className="font-medium text-sm truncate">{p.user_name}</div>
                                <div className="text-xs text-muted-foreground truncate">
                                  {p.user_email}
                                </div>
                              </div>
                              <div>
                                <div className="text-[10px] uppercase tracking-wider text-muted-foreground mb-0.5">
                                  Cantidad
                                </div>
                                <div className="font-medium text-sm">
                                  {p.quantity?.toLocaleString('es-AR')}
                                </div>
                                <div className="text-xs text-[#00E676]">
                                  Entregar: {p.delivered_quantity?.toLocaleString('es-AR')}
                                </div>
                              </div>
                            </div>
                            <div>
                              <div className="text-[10px] uppercase tracking-wider text-muted-foreground mb-0.5">
                                URL destino
                              </div>
                              <a
                                href={p.target_url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-xs text-primary hover:underline break-all"
                              >
                                {p.target_url}
                              </a>
                            </div>
                            <div className="grid grid-cols-2 gap-3">
                              <div>
                                <div className="text-[10px] uppercase tracking-wider text-muted-foreground mb-0.5">
                                  Pedido
                                </div>
                                <div className="font-mono text-xs">#{p.id.slice(0, 8)}</div>
                              </div>
                              <div>
                                <div className="text-[10px] uppercase tracking-wider text-muted-foreground mb-0.5">
                                  Garantía
                                </div>
                                <div className="text-xs font-medium">
                                  {p.warranty_days === 365
                                    ? '1 año ($14.500)'
                                    : p.warranty_days === 60
                                    ? '60 días ($5.100)'
                                    : p.warranty_days === 30
                                    ? '30 días ($2.000)'
                                    : '3 días (gratis)'}
                                </div>
                              </div>
                            </div>
                          </div>

                          <div className="p-4 rounded-xl bg-background border border-border mb-4">
                            <div className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1">
                              Monto a recibir
                            </div>
                            <div className="text-3xl font-bold text-primary">
                              ${(p.price || 0).toLocaleString('es-AR')}
                            </div>
                          </div>

                          <div className="flex gap-2">
                            <Button
                              onClick={() => approvePayment(p.id)}
                              className="flex-1 bg-[#00E676] hover:bg-[#00E676]/90 text-black font-semibold"
                              data-testid={`approve-payment-${p.id}`}
                            >
                              <CheckCircle size={18} className="mr-1.5" weight="fill" />
                              Aprobar y enviar
                            </Button>
                            <Button
                              variant="outline"
                              onClick={() => rejectPayment(p.id)}
                              className="border-destructive/30 text-destructive hover:bg-destructive/10"
                              data-testid={`reject-payment-${p.id}`}
                            >
                              <XCircle size={18} weight="fill" />
                            </Button>
                          </div>
                        </div>

                        {/* Columna 3-5: Comprobante + análisis */}
                        <div className="lg:col-span-3 p-6 bg-background">
                          {p.receipt_uploaded ? (
                            <div className="space-y-4">
                              <div className="flex items-center justify-between">
                                <div className="flex items-center gap-2">
                                  <ImageIcon size={18} weight="duotone" className="text-primary" />
                                  <span className="text-sm font-semibold">Comprobante</span>
                                </div>
                                <a
                                  href={`${API_URL}/api/admin/orders/${p.id}/receipt-image`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-xs text-primary hover:underline"
                                  data-testid={`open-receipt-${p.id}`}
                                >
                                  Abrir en tamaño completo →
                                </a>
                              </div>

                              <a
                                href={`${API_URL}/api/admin/orders/${p.id}/receipt-image`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="block group"
                              >
                                <img
                                  src={`${API_URL}/api/admin/orders/${p.id}/receipt-image`}
                                  alt="Comprobante"
                                  className="w-full max-h-96 object-contain bg-muted/30 rounded-xl border border-border group-hover:border-primary/40 transition-colors"
                                  data-testid={`receipt-image-${p.id}`}
                                />
                              </a>

                              <div className="space-y-2">
                                <div className="text-[10px] uppercase tracking-wider text-muted-foreground">
                                  Análisis automático (OCR)
                                </div>
                                <div className="grid grid-cols-3 gap-2">
                                  {ocrFlags.map((f) => (
                                    <div
                                      key={f.label}
                                      className={`p-3 rounded-lg border ${
                                        f.ok
                                          ? 'bg-[#00E676]/10 border-[#00E676]/30'
                                          : 'bg-destructive/10 border-destructive/30'
                                      }`}
                                    >
                                      <div className="flex items-center gap-1.5 mb-1">
                                        {f.ok ? (
                                          <CheckCircle size={14} weight="fill" className="text-[#00E676]" />
                                        ) : (
                                          <XCircle size={14} weight="fill" className="text-destructive" />
                                        )}
                                        <span className="text-[10px] uppercase tracking-wider text-muted-foreground">
                                          {f.label}
                                        </span>
                                      </div>
                                      <div className="text-xs font-semibold">
                                        {f.ok ? 'OK' : 'No detectado'}
                                      </div>
                                    </div>
                                  ))}
                                </div>

                                <div className="grid grid-cols-2 gap-2 text-xs">
                                  <div className="p-2.5 rounded-md bg-muted">
                                    <div className="text-[10px] uppercase tracking-wider text-muted-foreground mb-0.5">
                                      Monto detectado
                                    </div>
                                    <div className="font-semibold">
                                      ${(analysis.amount_detected || 0).toLocaleString('es-AR')}
                                    </div>
                                  </div>
                                  <div className="p-2.5 rounded-md bg-muted">
                                    <div className="text-[10px] uppercase tracking-wider text-muted-foreground mb-0.5">
                                      Fecha detectada
                                    </div>
                                    <div className="font-semibold">
                                      {analysis.date_detected || '—'}
                                    </div>
                                  </div>
                                  <div className="p-2.5 rounded-md bg-blue-500/10 border border-blue-500/20">
                                    <div className="text-[10px] uppercase tracking-wider text-blue-500 mb-0.5">
                                      🕐 Hora de transferencia
                                    </div>
                                    <div className="font-bold text-base">
                                      {analysis.time_detected ? `${analysis.time_detected} hs` : '—'}
                                    </div>
                                  </div>
                                  <div className="p-2.5 rounded-md bg-purple-500/10 border border-purple-500/20">
                                    <div className="text-[10px] uppercase tracking-wider text-purple-500 mb-0.5">
                                      👤 Titular que envía
                                    </div>
                                    <div className="font-bold text-sm truncate" title={analysis.sender_name || ''}>
                                      {analysis.sender_name || '—'}
                                    </div>
                                  </div>
                                  <div className="p-2.5 rounded-md bg-muted col-span-2">
                                    <div className="text-[10px] uppercase tracking-wider text-muted-foreground mb-0.5">
                                      Destinatario detectado
                                    </div>
                                    <div className="font-semibold truncate">
                                      {analysis.destination_name ||
                                        analysis.destination_alias ||
                                        analysis.destination_dni ||
                                        '—'}
                                    </div>
                                  </div>
                                </div>

                                {analysis.red_flags && analysis.red_flags.length > 0 && (
                                  <div className="p-3 rounded-lg bg-yellow-500/10 border border-yellow-500/30 text-xs">
                                    <div className="font-semibold text-yellow-600 mb-1">
                                      ⚠️ Banderas a revisar:
                                    </div>
                                    <ul className="list-disc list-inside text-muted-foreground space-y-0.5">
                                      {analysis.red_flags.map((f, i) => (
                                        <li key={i}>{f}</li>
                                      ))}
                                    </ul>
                                  </div>
                                )}

                                {analysis.reasoning && (
                                  <div className="text-xs text-muted-foreground italic p-2.5 bg-muted/50 rounded-md">
                                    {analysis.reasoning}
                                  </div>
                                )}
                              </div>
                            </div>
                          ) : (
                            <div className="flex flex-col items-center justify-center h-full min-h-[200px] p-6 rounded-xl border-2 border-dashed border-border">
                              <Receipt
                                size={40}
                                weight="duotone"
                                className="text-muted-foreground mb-3"
                              />
                              <p className="text-sm font-medium text-muted-foreground">
                                Esperando que el cliente suba el comprobante
                              </p>
                            </div>
                          )}
                        </div>
                      </div>
                    </Card>
                  );
                })}
              </>
            )}
          </TabsContent>

          {/* Orders Tab */}
          <TabsContent value="orders_legacy" className="space-y-4">
            {orders.map((order) => (
              <Card key={order.id} className="p-6 border-border">
                <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
                  <div>
                    <h3 className="font-semibold mb-1">{order.service_name}</h3>
                    <p className="text-sm text-muted-foreground">Usuario: {order.user_id}</p>
                    <p className="text-sm text-muted-foreground">Cantidad: {order.quantity}</p>
                  </div>
                  <div className="flex items-center gap-4">
                    <Badge>{order.status}</Badge>
                    <span className="font-bold">${order.price.toLocaleString('es-AR')}</span>
                  </div>
                </div>
              </Card>
            ))}
          </TabsContent>

          {/* Customers Tab - Registered vs Guests with discount management */}
          <TabsContent value="customers" className="space-y-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Registered customers */}
              <Card className="p-6 border-border" data-testid="registered-customers-section">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-2">
                    <UserCheck size={24} weight="duotone" className="text-primary" />
                    <h3 className="font-semibold">Clientes registrados</h3>
                  </div>
                  <Badge>{customers.registered.length}</Badge>
                </div>
                <p className="text-xs text-muted-foreground mb-4">
                  Usuarios que iniciaron sesión. Podés asignar descuentos personalizados.
                </p>
                <div className="space-y-3 max-h-[600px] overflow-y-auto">
                  {customers.registered.length === 0 ? (
                    <p className="text-sm text-muted-foreground text-center py-8">
                      Aún no hay clientes registrados
                    </p>
                  ) : (
                    customers.registered.map((c) => (
                      <div
                        key={c.id}
                        className="p-3 rounded-lg bg-muted/50 border border-border"
                        data-testid={`registered-customer-${c.id}`}
                      >
                        <div className="flex items-start justify-between gap-2 mb-2">
                          <div className="min-w-0">
                            <div className="font-medium truncate">{c.name}</div>
                            <div className="text-xs text-muted-foreground truncate">{c.email}</div>
                          </div>
                          {c.discount_percentage > 0 && (
                            <Badge className="bg-[#00E676]/15 text-[#00E676] border-0 shrink-0">
                              -{c.discount_percentage}%
                            </Badge>
                          )}
                        </div>
                        <div className="flex items-center gap-3 text-xs text-muted-foreground mb-3">
                          <span>{c.total_orders} pedidos</span>
                          <span>•</span>
                          <span>${(c.total_spent || 0).toLocaleString('es-AR')}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <div className="relative flex-1">
                            <Percent size={14} className="absolute left-2 top-1/2 -translate-y-1/2 text-muted-foreground" />
                            <Input
                              type="number"
                              min={0}
                              max={100}
                              placeholder="Descuento %"
                              defaultValue={c.discount_percentage || ''}
                              className="pl-7 h-8 text-sm"
                              onChange={(e) =>
                                setDiscountInputs((prev) => ({ ...prev, [c.id]: e.target.value }))
                              }
                              data-testid={`discount-input-${c.id}`}
                            />
                          </div>
                          <Button
                            size="sm"
                            onClick={() =>
                              saveUserDiscount(c.id, discountInputs[c.id] ?? c.discount_percentage ?? 0)
                            }
                            data-testid={`save-discount-${c.id}`}
                          >
                            Guardar
                          </Button>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </Card>

              {/* Guest buyers */}
              <Card className="p-6 border-border" data-testid="guest-customers-section">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-2">
                    <UserCircle size={24} weight="duotone" className="text-muted-foreground" />
                    <h3 className="font-semibold">Compraron sin registro</h3>
                  </div>
                  <Badge variant="secondary">{customers.guests.length}</Badge>
                </div>
                <p className="text-xs text-muted-foreground mb-4">
                  Clientes que compraron como invitados (sin crear cuenta).
                </p>
                <div className="space-y-3 max-h-[600px] overflow-y-auto">
                  {customers.guests.length === 0 ? (
                    <p className="text-sm text-muted-foreground text-center py-8">
                      Aún no hay compras de invitados
                    </p>
                  ) : (
                    customers.guests.map((g) => (
                      <div
                        key={g.email}
                        className="p-3 rounded-lg bg-muted/50 border border-border"
                      >
                        <div className="flex items-start justify-between gap-2 mb-1">
                          <div className="min-w-0">
                            <div className="font-medium truncate">{g.name || '—'}</div>
                            <div className="text-xs text-muted-foreground truncate">{g.email}</div>
                          </div>
                        </div>
                        <div className="flex items-center gap-3 text-xs text-muted-foreground">
                          <span>{g.total_orders} pedidos</span>
                          <span>•</span>
                          <span>${(g.total_spent || 0).toLocaleString('es-AR')}</span>
                        </div>
                        <p className="text-[10px] text-muted-foreground mt-2 italic">
                          Para asignarle descuento, pedile que se registre.
                        </p>
                      </div>
                    ))
                  )}
                </div>
              </Card>
            </div>
          </TabsContent>

          {/* Users Tab */}
          <TabsContent value="users_legacy" className="space-y-4">
            {users.map((user) => (
              <Card key={user.email} className="p-6 border-border">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="font-semibold mb-1">{user.name}</h3>
                    <p className="text-sm text-muted-foreground">{user.email}</p>
                  </div>
                  <Badge variant={user.role === 'admin' ? 'default' : 'secondary'}>
                    {user.role}
                  </Badge>
                </div>
              </Card>
            ))}
          </TabsContent>

          {/* Services Tab */}
          <TabsContent value="services_legacy" className="space-y-4">
            {services.map((service) => (
              <Card key={service.id} className="p-6 border-border">
                <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <h3 className="font-semibold">{service.name}</h3>
                      <Badge variant={service.enabled ? 'default' : 'secondary'}>
                        {service.enabled ? 'Activo' : 'Inactivo'}
                      </Badge>
                      <Badge variant="outline">{service.platform}</Badge>
                    </div>
                    <p className="text-sm text-muted-foreground mb-3">{service.description}</p>
                    {service.packages && (
                      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2">
                        {service.packages.map((pkg) => (
                          <div key={pkg.quantity} className="px-3 py-2 bg-muted rounded-lg text-xs">
                            <div className="font-semibold">{pkg.quantity.toLocaleString('es-AR')}</div>
                            <div className="text-primary">${pkg.price_ars.toLocaleString('es-AR')}</div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                  <Button
                    variant="outline"
                    onClick={() => toggleServiceStatus(service.id, service.enabled)}
                    data-testid={`toggle-service-${service.id}`}
                  >
                    {service.enabled ? 'Desactivar' : 'Activar'}
                  </Button>
                </div>
              </Card>
            ))}
          </TabsContent>

          {/* Settings Tab */}
          <TabsContent value="settings">
            <div className="space-y-6">
              <Card className="p-8 border-border">
                <h3 className="text-xl font-semibold mb-6">Configuración de API SMM</h3>
                <form
                  onSubmit={(e) => {
                    e.preventDefault();
                    const formData = new FormData(e.target);
                    updateSettings(formData.get('apiUrl'), formData.get('apiKey'));
                  }}
                  className="space-y-6"
                >
                  <div className="space-y-2">
                    <Label htmlFor="apiUrl">API URL</Label>
                    <Input
                      id="apiUrl"
                      name="apiUrl"
                      type="url"
                      defaultValue={settings.smm_api?.api_url || ''}
                      placeholder="https://api.smmapi.com"
                      data-testid="admin-api-url-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="apiKey">API Key</Label>
                    <Input
                      id="apiKey"
                      name="apiKey"
                      type="password"
                      defaultValue={settings.smm_api?.api_key || ''}
                      placeholder="Tu API Key"
                      data-testid="admin-api-key-input"
                    />
                  </div>
                  <Button type="submit" data-testid="admin-save-settings-button">
                    Guardar Configuración
                  </Button>
                </form>
              </Card>

              {/* Meta Pixel */}
              <Card className="p-8 border-border" data-testid="meta-pixel-settings-card">
                <div className="flex items-center justify-between mb-6 gap-4 flex-wrap">
                  <div>
                    <h3 className="text-xl font-semibold">Meta Pixel (Facebook / Instagram Ads)</h3>
                    <p className="text-sm text-muted-foreground mt-1">
                      Se disparan los siguientes eventos estándar de Meta:
                    </p>
                    <ul className="text-xs text-muted-foreground mt-2 space-y-0.5 list-disc list-inside">
                      <li><span className="font-mono">PageView</span> — en cada carga de página.</li>
                      <li><span className="font-mono">ViewContent</span> — al elegir un servicio.</li>
                      <li><span className="font-mono">AddToCart</span> — al elegir un paquete.</li>
                      <li><span className="font-mono">Lead</span> — al confirmar pedido como invitado.</li>
                      <li><span className="font-mono">InitiateCheckout</span> — al confirmar pedido.</li>
                      <li><span className="font-mono">AddPaymentInfo</span> — al ver los datos de transferencia.</li>
                      <li><span className="font-mono">Purchase</span> — al enviar el comprobante.</li>
                      <li><span className="font-mono">CompleteRegistration</span> — al crear cuenta.</li>
                      <li><span className="font-mono">Contact</span> — al iniciar chat por WhatsApp.</li>
                    </ul>
                  </div>
                  <div className="text-right">
                    <div className="text-[10px] uppercase text-muted-foreground tracking-wider">
                      Estado
                    </div>
                    <div
                      className={`text-sm font-semibold ${
                        tracking.active !== false ? 'text-[#00E676]' : 'text-muted-foreground'
                      }`}
                      data-testid="meta-pixel-status"
                    >
                      {tracking.active !== false ? '● Activo' : '○ Desactivado'}
                    </div>
                  </div>
                </div>
                <form
                  onSubmit={(e) => {
                    e.preventDefault();
                    const fd = new FormData(e.target);
                    const raw = String(fd.get('pixelId') || '').trim();
                    if (raw && !/^\d+$/.test(raw)) {
                      toast.error('El Pixel ID debe ser numérico');
                      return;
                    }
                    updateTracking({ meta_pixel_id: raw });
                  }}
                  className="space-y-4"
                >
                  <div className="space-y-2">
                    <Label htmlFor="pixelId">Pixel ID</Label>
                    <Input
                      id="pixelId"
                      name="pixelId"
                      type="text"
                      inputMode="numeric"
                      pattern="[0-9]*"
                      key={tracking.meta_pixel_id || 'empty'}
                      defaultValue={tracking.meta_pixel_id || ''}
                      placeholder="Ej: 940049328402164"
                      data-testid="admin-meta-pixel-input"
                    />
                    <p className="text-xs text-muted-foreground">
                      Cambiá este ID si querés apuntar el tracking a otro pixel. El cambio se aplica
                      la próxima vez que un visitante entre a la web.
                    </p>
                  </div>
                  <div className="flex flex-wrap gap-3">
                    <Button type="submit" data-testid="admin-save-pixel-button">
                      Guardar Pixel
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => updateTracking({ active: !(tracking.active !== false) })}
                      data-testid="admin-toggle-pixel-button"
                    >
                      {tracking.active !== false ? 'Desactivar tracking' : 'Activar tracking'}
                    </Button>
                  </div>
                </form>
              </Card>
            </div>
          </TabsContent>

          {/* Branding Tab */}
          <TabsContent value="branding">
            <Card className="p-8 border-border">
              <h3 className="text-xl font-semibold mb-4">Personalización de Marca</h3>
              <p className="text-muted-foreground">
                Esta funcionalidad estará disponible próximamente. Podrás personalizar el nombre de la
                marca, colores y logotipo.
              </p>
            </Card>
          </TabsContent>
        </Tabs>
        </main>
      </div>
    </div>
  );
};